import { fromKyivWall, kyivNow, toKyivWall } from './kyivTime';

// Расписание рассылки: из правила («по понедельникам и средам в 10:00 до 10 ноября»)
// получается список конкретных моментов. Всё считается по киевскому времени, где бы ни был
// менеджер (kyivTime.js): моменты — киевские часы, серверу они уходят настоящим временем.
//
// Время можно задать одно на все отправки (timeMode 'same', поле time) или своё для каждой
// (timeMode 'each', поле times). «Каждая» зависит от режима: для списка дат это дата, для
// дней недели день недели, для чисел месяца число (или 'last').

export const MAX_OCCURRENCES = 60;

// Сервер не принимает отправку раньше, чем через 30 секунд, и дальше чем через год.
const MIN_AHEAD_MS = 60 * 1000;
const MAX_AHEAD_MS = 365 * 24 * 60 * 60 * 1000;
const MAX_SCAN_DAYS = 400;

export const REPEATS = [
  { value: 'once', label: 'Один раз' },
  { value: 'dates', label: 'Несколько дат' },
  { value: 'weekly', label: 'По дням недели' },
  { value: 'monthly', label: 'По числам месяца' },
];

// Порядок как в календаре: с понедельника. id совпадает с Date#getDay (воскресенье = 0).
export const WEEKDAYS = [
  { id: 1, short: 'Пн' },
  { id: 2, short: 'Вт' },
  { id: 3, short: 'Ср' },
  { id: 4, short: 'Чт' },
  { id: 5, short: 'Пт' },
  { id: 6, short: 'Сб' },
  { id: 0, short: 'Вс' },
];

const WEEKDAY_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const pad = (n) => `0${n}`.slice(-2);

export const dateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// '2026-10-15' -> дата в полночь по местному времени, или null, если такой даты нет.
export const parseKey = (key) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || '');
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const same =
    date.getFullYear() === Number(match[1]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[3]);
  return same ? date : null;
};

const parseTime = (time) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time || '');
  return match ? { hours: Number(match[1]), minutes: Number(match[2]) } : null;
};

const addDays = (date, count) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + count);

const shortDate = (date) => `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
const shortKey = (key) => {
  const date = parseKey(key);
  return date ? shortDate(date) : key;
};

// Первая подходящая отправка по умолчанию: сегодня в 10:00, если это время ещё впереди, иначе
// сегодня в начале следующего часа, а если сегодня часов уже не осталось, то завтра в 10:00.
const firstDefault = (now) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tenToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 10, 0);
  if (tenToday - now >= MIN_AHEAD_MS) return { day: today, time: '10:00' };
  const nextHour = now.getHours() + 1;
  if (nextHour <= 23) return { day: today, time: `${pad(nextHour)}:00` };
  return { day: addDays(today, 1), time: '10:00' };
};

export const defaultRule = (now = kyivNow()) => {
  const { day, time } = firstDefault(now);
  return {
    repeat: 'once',
    timeMode: 'same',
    time,
    times: {},
    date: dateKey(day),
    dates: [],
    weekdays: [],
    monthDays: [],
    from: dateKey(day),
    to: dateKey(addDays(day, 28)),
  };
};

const isLastDayOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() === date.getDate();

// Каждый день периода от from до to включительно.
const daysBetween = (fromKey, toKey) => {
  const from = parseKey(fromKey);
  const to = parseKey(toKey);
  if (!from || !to || to < from) return [];
  const days = [];
  for (let day = from; day <= to && days.length < MAX_SCAN_DAYS; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
};

const daysOf = (rule) => {
  switch (rule.repeat) {
    case 'once': {
      const day = parseKey(rule.date);
      return day ? [day] : [];
    }
    case 'dates':
      return (rule.dates || []).map(parseKey).filter(Boolean);
    case 'weekly':
      return daysBetween(rule.from, rule.to).filter((day) =>
        (rule.weekdays || []).includes(day.getDay())
      );
    case 'monthly':
      return daysBetween(rule.from, rule.to).filter(
        (day) =>
          (rule.monthDays || []).includes(day.getDate()) ||
          ((rule.monthDays || []).includes('last') && isLastDayOfMonth(day))
      );
    default:
      return [];
  }
};

// Ключ, под которым хранится время этого дня, когда время у каждого своё.
const slotKeyOf = (rule, day) => {
  switch (rule.repeat) {
    case 'dates':
      return dateKey(day);
    case 'weekly':
      return String(day.getDay());
    case 'monthly':
      return (rule.monthDays || []).includes(day.getDate()) ? String(day.getDate()) : 'last';
    default:
      return 'once';
  }
};

// Время одного дня: общее или своё. Если своё ещё не задано, берётся общее.
const timeOfDay = (rule, day) =>
  rule.timeMode === 'each' && rule.repeat !== 'once'
    ? (rule.times || {})[slotKeyOf(rule, day)] || rule.time
    : rule.time;

// Что можно настроить по отдельности: для каждого такого «слота» своё время.
// [{ key, label }], по порядку.
export const timeSlots = (rule) => {
  switch (rule.repeat) {
    case 'dates':
      return (rule.dates || [])
        .map(parseKey)
        .filter(Boolean)
        .sort((a, b) => a - b)
        .map((date) => ({
          key: dateKey(date),
          label: `${WEEKDAY_SHORT[date.getDay()]} ${shortDate(date)}`,
        }));
    case 'weekly':
      return WEEKDAYS.filter((day) => (rule.weekdays || []).includes(day.id)).map((day) => ({
        key: String(day.id),
        label: day.short,
      }));
    case 'monthly': {
      const slots = (rule.monthDays || [])
        .filter((day) => day !== 'last')
        .sort((a, b) => a - b)
        .map((day) => ({ key: String(day), label: `${day}-е число` }));
      if ((rule.monthDays || []).includes('last')) {
        slots.push({ key: 'last', label: 'Последнее число' });
      }
      return slots;
    }
    default:
      return [];
  }
};

// Время слота в окне настройки (своё, а если не задано, то общее).
export const slotTime = (rule, key) => (rule.times || {})[key] || rule.time;

const emptyChoice = (rule) => {
  switch (rule.repeat) {
    case 'once':
      return !parseKey(rule.date) ? 'Укажите дату.' : '';
    case 'dates':
      return (rule.dates || []).length === 0 ? 'Выберите хотя бы одну дату.' : '';
    case 'weekly':
      return (rule.weekdays || []).length === 0 ? 'Выберите дни недели.' : '';
    case 'monthly':
      return (rule.monthDays || []).length === 0 ? 'Выберите числа месяца.' : '';
    default:
      return 'Выберите, как повторять рассылку.';
  }
};

// Моменты отправки по правилу. Прошедшие и слишком далёкие не попадают в список, но
// считаются отдельно, чтобы можно было объяснить, куда они делись.
// Возвращает { list: Date[], past, tooFar, problem }.
export const buildOccurrences = (rule, now = kyivNow()) => {
  const choice = emptyChoice(rule);
  if (choice) return { list: [], past: 0, tooFar: 0, problem: choice };

  if (
    (rule.repeat === 'weekly' || rule.repeat === 'monthly') &&
    daysBetween(rule.from, rule.to).length === 0
  ) {
    return {
      list: [],
      past: 0,
      tooFar: 0,
      problem: 'Укажите период: «с» не может быть позже «по».',
    };
  }

  const moments = [];
  const seen = new Set();
  const days = daysOf(rule);
  for (let index = 0; index < days.length; index += 1) {
    const day = days[index];
    const time = parseTime(timeOfDay(rule, day));
    if (!time) return { list: [], past: 0, tooFar: 0, problem: 'Укажите время.' };
    const moment = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      time.hours,
      time.minutes
    );
    if (!seen.has(moment.getTime())) {
      seen.add(moment.getTime());
      moments.push(moment);
    }
  }
  moments.sort((a, b) => a - b);

  const past = moments.filter((moment) => moment - now < MIN_AHEAD_MS).length;
  const tooFar = moments.filter((moment) => moment - now > MAX_AHEAD_MS).length;
  const list = moments.filter(
    (moment) => moment - now >= MIN_AHEAD_MS && moment - now <= MAX_AHEAD_MS
  );

  let problem = '';
  if (list.length === 0) {
    problem = 'В этом выборе нет ни одной будущей отправки.';
  } else if (list.length > MAX_OCCURRENCES) {
    problem = `Слишком много отправок (${list.length}): можно не больше ${MAX_OCCURRENCES}. Сократите период.`;
  }
  return { list, past, tooFar, problem };
};

const clockOf = (moment) => `${pad(moment.getHours())}:${pad(moment.getMinutes())}`;

// Время у слотов правила: общее, если у всех одно, иначе своё у каждого слота. null, если у
// одного слота (например, понедельника) встречается разное время: такое правило не описать.
const slotTimes = (sorted, slotOf) => {
  const times = {};
  for (const moment of sorted) {
    const key = slotOf(moment);
    if (times[key] && times[key] !== clockOf(moment)) return null;
    times[key] = clockOf(moment);
  }
  const distinct = new Set(Object.values(times));
  return { time: clockOf(sorted[0]), timeMode: distinct.size > 1 ? 'each' : 'same', times: distinct.size > 1 ? times : {} };
};

// Даёт ли правило ровно эти моменты (не больше и не меньше).
const sameMoments = (rule, sorted, now) => {
  const list = buildOccurrences(rule, now).list;
  return list.length === sorted.length && list.every((moment, index) => moment.getTime() === sorted[index].getTime());
};

// По дням недели или по числам месяца, как подсказывает подпись серии (её пишет describeRule).
// null, если подпись другая или из моментов такое правило не собрать.
const repeatedRule = (rule, sorted, label, now) => {
  const text = String(label || '');
  const base = { ...rule, from: dateKey(sorted[0]), to: dateKey(sorted[sorted.length - 1]) };
  let candidate = null;
  if (text.startsWith('Числа:')) {
    const wantsLast = /последнее/.test(text);
    const slotOf = (moment) => (wantsLast && isLastDayOfMonth(moment) ? 'last' : String(moment.getDate()));
    const slots = slotTimes(sorted, slotOf);
    if (!slots) return null;
    const days = Array.from(new Set(sorted.map(slotOf)));
    candidate = {
      ...base,
      repeat: 'monthly',
      monthDays: days.map((day) => (day === 'last' ? 'last' : Number(day))),
      ...slots,
    };
  } else if (!text.startsWith('Даты:') && WEEKDAY_SHORT.some((day) => text.startsWith(day))) {
    const slotOf = (moment) => String(moment.getDay());
    const slots = slotTimes(sorted, slotOf);
    if (!slots) return null;
    candidate = { ...base, repeat: 'weekly', weekdays: Array.from(new Set(sorted.map((m) => m.getDay()))), ...slots };
  }
  return candidate && sameMoments(candidate, sorted, now) ? candidate : null;
};

// Правило из готовых моментов: так открывается окно правки уже созданной рассылки. Одна отправка
// это «Один раз»; серия по дням недели или по числам месяца открывается в своём режиме (режим
// берётся из подписи серии `label`); всё остальное как «несколько дат», время общее, если у всех
// одинаковое, иначе своё у каждой даты.
export const ruleFromMoments = (moments, now = kyivNow(), label = '') => {
  const rule = defaultRule(now);
  const sorted = moments.slice().sort((a, b) => a - b);
  const clock = clockOf;
  // Сначала подпись: серия по дням недели или по числам месяца может давать и одну отправку
  // (например, 31-е число в ноябре), и всё равно открывается в своём режиме.
  if (sorted.length > 0) {
    const repeated = repeatedRule(rule, sorted, label, now);
    if (repeated) return repeated;
  }
  // Одна отправка открывается так же, как её создавали: «Один раз», с её датой и временем.
  if (sorted.length === 1) {
    return { ...rule, repeat: 'once', date: dateKey(sorted[0]), time: clock(sorted[0]) };
  }
  const times = {};
  sorted.forEach((moment) => {
    times[dateKey(moment)] = clock(moment);
  });
  const distinct = new Set(Object.keys(times).map((key) => times[key]));
  return {
    ...rule,
    repeat: 'dates',
    dates: Object.keys(times).sort(),
    time: sorted.length > 0 ? clock(sorted[0]) : rule.time,
    timeMode: distinct.size > 1 ? 'each' : 'same',
    times,
  };
};

// Короткая подпись расписания: она хранится у рассылок серии и видна в истории.
export const describeRule = (rule, list = []) => {
  const each = rule.timeMode === 'each' && rule.repeat !== 'once';
  const at = (key) => (each ? slotTime(rule, key) : rule.time);
  const period = `(${shortKey(rule.from)}–${shortKey(rule.to)})`;
  let text = '';
  if (rule.repeat === 'dates') {
    const dates = (rule.dates || []).slice().sort();
    const shown = dates
      .slice(0, 6)
      .map((key) => (each ? `${shortKey(key)} ${at(key)}` : shortKey(key)));
    text = `Даты: ${shown.join(', ')}${dates.length > 6 ? '…' : ''}${each ? '' : ` в ${rule.time}`}`;
  } else if (rule.repeat === 'weekly') {
    const days = WEEKDAYS.filter((day) => (rule.weekdays || []).includes(day.id)).map((day) =>
      each ? `${day.short} ${at(String(day.id))}` : day.short
    );
    text = `${days.join(', ')}${each ? '' : ` в ${rule.time}`} ${period}`;
  } else if (rule.repeat === 'monthly') {
    const numbers = (rule.monthDays || [])
      .filter((day) => day !== 'last')
      .sort((a, b) => a - b)
      .map((day) => (each ? `${day} ${at(String(day))}` : String(day)));
    if ((rule.monthDays || []).includes('last')) {
      numbers.push(each ? `последнее ${at('last')}` : 'последнее');
    }
    text = `Числа: ${numbers.join(', ')}${each ? '' : ` в ${rule.time}`} ${period}`;
  } else if (list.length > 0) {
    text = `${shortDate(list[0])} в ${rule.time}`;
  }
  return text.slice(0, 128);
};

// То, что уходит серверу: один момент (sendAt) или список (sendAtList) с подписью. Моменты
// расписания — киевские часы, серверу уходит настоящее время (kyivTime.js).
export const payloadOf = (rule, list) => {
  if (rule.repeat === 'once') {
    return list.length > 0 ? { sendAt: fromKyivWall(list[0]).toISOString() } : {};
  }
  return {
    sendAtList: list.map((moment) => fromKyivWall(moment).toISOString()),
    scheduleLabel: describeRule(rule, list),
  };
};

// Новые времена для серии, часть которой уже ушла (keptSendAts — их настоящие моменты, ISO).
// Сервер получает только новые времена, тоже настоящим временем, а подпись описывает всю
// серию, вместе с тем, что уже отправлено.
export const remainingPayloadOf = (keptSendAts, list) => {
  const all = keptSendAts.map((iso) => toKyivWall(iso)).concat(list);
  return {
    sendAtList: list.map((moment) => fromKyivWall(moment).toISOString()),
    scheduleLabel: describeRule(ruleFromMoments(all), all),
  };
};

// «Пн 15.10 в 10:00»
export const formatMoment = (moment) =>
  `${WEEKDAY_SHORT[moment.getDay()]} ${shortDate(moment)} в ${pad(moment.getHours())}:${pad(
    moment.getMinutes()
  )}`;

// «15.10.2026, 10:00»
export const formatFull = (moment) =>
  `${shortDate(moment)}.${moment.getFullYear()}, ${pad(moment.getHours())}:${pad(
    moment.getMinutes()
  )}`;
